import { useState } from "react";
import { Check, Copy, Mail } from "lucide-react";
import type { MailAddress } from "@/api/mailApi";
import { useStatusAnnouncer } from "@/components/StatusAnnouncer";
import { cn } from "@/lib/utils";

/**
 * Outlook-style recipient pill. Shows the display name, exposes the email
 * address on hover (native `title=` plus a small popover), and copies the
 * raw email to the clipboard on click. Text inside the pill is selectable
 * so the address can be drag-selected too.
 */
export function RecipientChip({
    address,
    variant = "default"
}: {
    address: MailAddress;
    /** "plain" renders Outlook-style inline text (no pill, no icon) — used
     *  on the reading pane's To: line. */
    variant?: "default" | "from" | "plain";
}) {
    const announce = useStatusAnnouncer().announce;
    const [copied, setCopied] = useState(false);

    const display = address.name?.trim() || address.email;
    const tooltip = address.email;

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(address.email);
            setCopied(true);
            announce(`Copied ${address.email}`);
            setTimeout(() => setCopied(false), 1500);
        } catch {
            // Older browsers without async clipboard — fall back to a manual
            // textarea + execCommand. We can add it later if anyone hits it.
        }
    };

    return (
        <span
            className={cn(
                "group/chip relative inline-flex items-center gap-1",
                "select-text cursor-default",
                variant === "plain"
                    ? "rounded px-0.5 text-foreground/90 hover:bg-accent"
                    : "rounded-full border px-2 py-0.5 text-xs hover:bg-accent",
                variant === "from" && "border-primary/30 bg-primary/5 text-foreground",
                variant === "default" && "border-input bg-muted/40"
            )}
            title={`${display} <${tooltip}>`}
            data-testid="recipient-chip"
        >
            {variant === "plain" ? null : (
                <Mail className="h-3 w-3 shrink-0 text-muted-foreground" aria-hidden="true" />
            )}
            <span className="select-text">{display}</span>
            <button
                type="button"
                onClick={copy}
                aria-label={`Copy ${address.email}`}
                title={copied ? "Copied" : `Copy ${address.email}`}
                className="ml-0.5 flex h-4 w-4 items-center justify-center rounded text-muted-foreground opacity-0 transition-opacity hover:bg-muted hover:text-foreground group-hover/chip:opacity-100 focus:opacity-100"
            >
                {copied ? <Check className="h-3 w-3 text-emerald-600" /> : <Copy className="h-3 w-3" />}
            </button>
            {/* Email pop-out — shown only on hover/focus, selectable for
                drag-copy. Keeps the chip compact in the resting state. */}
            <span
                className="pointer-events-none absolute left-1/2 top-full z-10 mt-1 hidden -translate-x-1/2 rounded-md border bg-popover px-2 py-1 text-[10px] text-popover-foreground shadow-md group-hover/chip:block"
                role="presentation"
            >
                <span className="select-all font-mono">{tooltip}</span>
            </span>
        </span>
    );
}
